import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { QrScannerPanel } from "../components/QrScannerPanel";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { getAttendanceFeatureLabel, isScannerAttendanceFeature } from "../lib/attendance";
import { extractTime, formatDate, formatHours, formatMinutes, getErrorMessage } from "../lib/formatters";


const localDateValue = () => new Date().toLocaleDateString("en-CA");

const managerNavItems = [
  { path: "/manager/dashboard", label: "Manager Dashboard", shortLabel: "HOME", testId: "manager-dashboard" },
  { path: "/manager/team", label: "Team Attendance", shortLabel: "TEAM", testId: "manager-team" },
  { path: "/manager/approvals", label: "Approvals", shortLabel: "QUEUE", testId: "manager-approvals" },
];


export default function EmployeePage({ section }) {
  const navigate = useNavigate();
  const { user, tenant, featureFlags, employee: authEmployee, leaveEnabled: authLeaveEnabled, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [balances, setBalances] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [regularizations, setRegularizations] = useState([]);
  const [leaveForm, setLeaveForm] = useState({ leave_type_code: "PL", start_date: localDateValue(), end_date: localDateValue(), reason: "" });
  const [regularizationForm, setRegularizationForm] = useState({ date: localDateValue(), requested_clock_in_time: "09:30", requested_clock_out_time: "18:30", reason: "" });
  const [scanToken, setScanToken] = useState("");

  const assignedFeature = dashboard?.employee?.assigned_attendance_feature || today?.employee?.assigned_attendance_feature || authEmployee?.assigned_attendance_feature;
  const assignedFeatureLabel = dashboard?.employee?.assigned_attendance_label || today?.employee?.assigned_attendance_label || authEmployee?.assigned_attendance_label || getAttendanceFeatureLabel(assignedFeature);
  const activeQuickAction = dashboard?.quick_action || today?.quick_action;
  const tenantHasScanFeature = Boolean(featureFlags?.kiosk_qr_attendance || featureFlags?.employee_qr_scan_attendance);
  const showScannerNav = tenantHasScanFeature && isScannerAttendanceFeature(assignedFeature);
  const leaveModuleEnabled = dashboard?.leave_enabled ?? authLeaveEnabled;

  const navItems = useMemo(() => {
    const selfServiceItems = [
      { path: "/employee/dashboard", label: "My Dashboard", shortLabel: "HOME", testId: "employee-dashboard" },
      { path: "/employee/attendance", label: "My Attendance", shortLabel: "TIME", testId: "employee-attendance" },
      { path: "/employee/regularization", label: "Regularization", shortLabel: "FIX", testId: "employee-regularization" },
    ];
    if (leaveModuleEnabled) {
      selfServiceItems.splice(2, 0, { path: "/employee/leave", label: "Leave & Balance", shortLabel: "LEAVE", testId: "employee-leave" });
    }
    if (showScannerNav) {
      selfServiceItems.push({ path: "/employee/scan", label: "QR Scan", shortLabel: "QR", testId: "employee-scan" });
    }
    return user?.role === "manager" ? [...managerNavItems, ...selfServiceItems] : selfServiceItems;
  }, [leaveModuleEnabled, showScannerNav, user?.role]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (section === "dashboard") {
        const [dashboardResponse, historyResponse] = await Promise.all([api.get("/employee/dashboard"), api.get("/attendance/history", { params: { limit: 7 } })]);
        setDashboard(dashboardResponse.data);
        setHistory(historyResponse.data.records);
      }
      if (section === "attendance") {
        const [todayResponse, historyResponse] = await Promise.all([api.get("/attendance/today"), api.get("/attendance/history", { params: { limit: 31 } })]);
        setToday(todayResponse.data);
        setHistory(historyResponse.data.records);
      }
      if (section === "leave") {
        if (authLeaveEnabled) {
          const [balanceResponse, requestResponse] = await Promise.all([api.get("/leaves/balances"), api.get("/leaves/requests")]);
          setBalances(balanceResponse.data);
          setLeaveRequests(requestResponse.data);
          if (balanceResponse.data[0]) {
            setLeaveForm((current) => ({ ...current, leave_type_code: balanceResponse.data[0].leave_type_code }));
          }
        } else {
          setBalances([]);
          setLeaveRequests([]);
        }
      }
      if (section === "regularization") {
        const [regularizationResponse, historyResponse] = await Promise.all([api.get("/attendance/regularizations"), api.get("/attendance/history", { params: { limit: 15 } })]);
        setRegularizations(regularizationResponse.data);
        setHistory(historyResponse.data.records);
      }
      if (section === "scan") {
        const todayResponse = await api.get("/attendance/today");
        setToday(todayResponse.data);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [section, authLeaveEnabled]);

  const handleClock = async (source = "web") => {
    try {
      await api.post("/attendance/clock", { source });
      toast.success("Attendance updated");
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handlePrimaryAction = () => {
    if (!activeQuickAction) return;
    if (activeQuickAction.type === "disabled") {
      toast.error(activeQuickAction.error);
      return;
    }
    if (activeQuickAction.type === "scanner") {
      navigate("/employee/scan");
      return;
    }
    handleClock("web");
  };

  const handleLeaveSubmit = async (event) => {
    event.preventDefault();
    try {
      await api.post("/leaves/requests", leaveForm);
      toast.success("Leave request submitted");
      setLeaveForm((current) => ({ ...current, reason: "" }));
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const cancelLeave = async (requestId) => {
    try {
      await api.put(`/leaves/requests/${requestId}/cancel`);
      toast.success("Leave request canceled");
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const submitRegularization = async (event) => {
    event.preventDefault();
    try {
      await api.post("/attendance/regularizations", regularizationForm);
      toast.success("Regularization submitted");
      setRegularizationForm((current) => ({ ...current, reason: "" }));
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const goToScan = () => {
    if (!scanToken) return;
    navigate(`/scan/${scanToken}`);
  };

  const handleScanDetected = (decodedText) => {
    const token = decodedText.split("/").filter(Boolean).pop();
    if (!token) {
      toast.error("Could not read a valid QR token");
      return;
    }
    navigate(`/scan/${token}`);
  };

  const renderDashboard = () => (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="employee-dashboard-stats-grid">
        <StatCard label="Today" value={dashboard?.today?.status || "Absent"} accent="#0D1B2A" helper="Your live punch status" testId="employee-stat-today" />
        <StatCard label="Hours" value={formatHours(dashboard?.today?.record?.total_hours || 0)} accent="#F4A300" helper="Worked today so far" testId="employee-stat-hours" />
        <StatCard label="Late" value={formatMinutes(dashboard?.today?.record?.late_minutes || 0)} accent="#F59E0B" helper="Late minutes today" testId="employee-stat-late" />
        {leaveModuleEnabled ? <StatCard label="Balances" value={dashboard?.leave_balances?.length || 0} accent="#0D1B2A" helper="Tracked leave buckets" testId="employee-stat-balances" /> : null}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className="border-slate-200/70 bg-white/85" data-testid="employee-quick-actions-card">
          <CardHeader>
            <CardTitle data-testid="employee-quick-actions-title">Quick actions</CardTitle>
            <CardDescription data-testid="employee-quick-actions-description">Clock attendance and move into the next workflow from one place.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid="employee-today-status-card">
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold text-slate-900">Today’s attendance</p>
                <StatusBadge value={dashboard?.today?.status || "Absent"} testId="employee-today-status-badge" />
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="employee-assignment-summary-row">
                <span className="inline-flex rounded-full bg-[#0D1B2A] px-3 py-1 text-xs font-semibold text-white" data-testid="employee-assignment-tag">{activeQuickAction?.tag || assignedFeatureLabel}</span>
                <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600" data-testid="employee-assignment-label">{assignedFeatureLabel}</span>
              </div>
              <p className="mt-3 text-sm text-slate-500" data-testid="employee-quick-action-copy">{activeQuickAction?.type === "scanner" ? "Your home action opens the QR scanner flow linked to your assigned attendance method." : "Your browser punch flow follows the attendance method assigned by your admin."}</p>
              {activeQuickAction?.error ? <p className="mt-3 text-sm font-semibold text-rose-600" data-testid="employee-quick-action-error">{activeQuickAction.error}</p> : null}
              <Button type="button" className="mt-4 rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={handlePrimaryAction} disabled={dashboard?.today?.status === "On Leave" || activeQuickAction?.type === "disabled"} data-testid="employee-quick-clock-button">
                {activeQuickAction?.label || (dashboard?.today?.status === "In" ? "Clock Out" : "Clock In")}
              </Button>
            </div>
            {leaveModuleEnabled ? (
              <div className="grid gap-3 md:grid-cols-2">
                {dashboard?.leave_balances?.map((balance) => (
                  <div key={balance.id} className="rounded-3xl border border-slate-200 bg-white p-4" data-testid={`employee-dashboard-balance-${balance.leave_type_code}`}>
                    <p className="text-sm font-semibold text-slate-900">{balance.leave_type_name}</p>
                    <p className="mt-2 text-2xl font-extrabold text-slate-900">{balance.remaining}</p>
                    <p className="text-sm text-slate-500">remaining</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500" data-testid="employee-leave-disabled-dashboard-card">
                Leave self-service will appear here after your Company Admin configures leave settings for your company.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 bg-white/85" data-testid="employee-recent-history-card">
          <CardHeader>
            <CardTitle data-testid="employee-recent-history-title">Recent attendance history</CardTitle>
            <CardDescription data-testid="employee-recent-history-description">Check your latest punches, hours, and exceptions.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {history.map((record) => (
              <div key={record.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`employee-history-card-${record.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-900">{formatDate(record.date)}</p>
                  <StatusBadge value={record.status} testId={`employee-history-status-${record.id}`} />
                </div>
                <p className="mt-2 text-sm text-slate-500">{extractTime(record.clock_in)} → {extractTime(record.clock_out)} • {formatHours(record.total_hours)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </>
  );

  const renderAttendance = () => (
    <>
      <Card className="border-slate-200/70 bg-white/85" data-testid="employee-attendance-card">
        <CardHeader>
          <CardTitle data-testid="employee-attendance-title">Today’s punch</CardTitle>
          <CardDescription data-testid="employee-attendance-description">This punch action follows your assigned attendance method and tenant feature flags.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <StatusBadge value={today?.status || "Absent"} testId="employee-attendance-status-badge" />
            <p className="text-sm text-slate-500" data-testid="employee-attendance-clock-row">Clock in: {extractTime(today?.record?.clock_in)} • Clock out: {extractTime(today?.record?.clock_out)}</p>
            <p className="text-sm text-slate-500" data-testid="employee-attendance-metrics-row">Hours: {formatHours(today?.record?.total_hours || 0)} • Late: {formatMinutes(today?.record?.late_minutes || 0)}</p>
            <p className="text-sm font-semibold text-slate-700" data-testid="employee-attendance-assignment-label">Assigned method: {today?.employee?.assigned_attendance_label || assignedFeatureLabel}</p>
            {today?.quick_action?.error ? <p className="text-sm font-semibold text-rose-600" data-testid="employee-attendance-assignment-error">{today.quick_action.error}</p> : null}
          </div>
          <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={handlePrimaryAction} disabled={today?.status === "On Leave" || today?.quick_action?.type === "disabled"} data-testid="employee-attendance-clock-button">
            {today?.quick_action?.label || (today?.status === "In" ? "Clock Out" : "Clock In")}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200/70 bg-white/85" data-testid="employee-attendance-history-card">
        <CardHeader>
          <CardTitle data-testid="employee-attendance-history-title">Attendance history</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-3xl border border-slate-200/70" data-testid="employee-attendance-history-table-wrap">
            <Table data-testid="employee-attendance-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Late</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((record) => (
                  <TableRow key={record.id} data-testid={`employee-attendance-row-${record.id}`}>
                    <TableCell>{formatDate(record.date)}</TableCell>
                    <TableCell>{extractTime(record.clock_in)}</TableCell>
                    <TableCell>{extractTime(record.clock_out)}</TableCell>
                    <TableCell>{formatHours(record.total_hours)}</TableCell>
                    <TableCell>{formatMinutes(record.late_minutes)}</TableCell>
                    <TableCell>{record.source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );

  const renderLeave = () => (
    !leaveModuleEnabled ? (
      <Card className="border-slate-200/70 bg-white/85 p-10 text-center text-slate-500" data-testid="employee-leave-disabled-card">
        Leave management is not enabled for your company yet.
      </Card>
    ) : (
    <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      <Card className="border-slate-200/70 bg-white/85" data-testid="employee-leave-form-card">
        <CardHeader>
          <CardTitle data-testid="employee-leave-form-title">Apply for leave</CardTitle>
          <CardDescription data-testid="employee-leave-form-description">Leave overlap prevention and balance checks are enforced on submission.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleLeaveSubmit} data-testid="employee-leave-form">
            <select className="h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm" value={leaveForm.leave_type_code} onChange={(event) => setLeaveForm((current) => ({ ...current, leave_type_code: event.target.value }))} data-testid="employee-leave-type-select">
              {balances.map((balance) => <option key={balance.leave_type_code} value={balance.leave_type_code}>{balance.leave_type_name}</option>)}
            </select>
            <div className="grid gap-3 md:grid-cols-2">
              <Input type="date" value={leaveForm.start_date} onChange={(event) => setLeaveForm((current) => ({ ...current, start_date: event.target.value }))} data-testid="employee-leave-start-input" />
              <Input type="date" value={leaveForm.end_date} onChange={(event) => setLeaveForm((current) => ({ ...current, end_date: event.target.value }))} data-testid="employee-leave-end-input" />
            </div>
            <Input value={leaveForm.reason} onChange={(event) => setLeaveForm((current) => ({ ...current, reason: event.target.value }))} placeholder="Reason" data-testid="employee-leave-reason-input" />
            <Button type="submit" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" data-testid="employee-leave-submit-button">Submit leave request</Button>
          </form>
        </CardContent>
      </Card>

      <div className="grid gap-6">
        <Card className="border-slate-200/70 bg-white/85" data-testid="employee-leave-balances-card">
          <CardHeader>
            <CardTitle data-testid="employee-leave-balances-title">Leave balances</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            {balances.map((balance) => (
              <div key={balance.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`employee-balance-card-${balance.leave_type_code}`}>
                <p className="font-semibold text-slate-900">{balance.leave_type_name}</p>
                <p className="mt-2 text-2xl font-extrabold text-slate-900">{balance.remaining}</p>
                <p className="text-sm text-slate-500">Remaining of {balance.total}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200/70 bg-white/85" data-testid="employee-leave-requests-card">
          <CardHeader>
            <CardTitle data-testid="employee-leave-requests-title">My leave requests</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {leaveRequests.map((request) => (
              <div key={request.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`employee-leave-request-${request.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-900">{request.leave_type_name}</p>
                  <StatusBadge value={request.status} testId={`employee-leave-request-status-${request.id}`} />
                </div>
                <p className="mt-2 text-sm text-slate-500">{formatDate(request.start_date)} to {formatDate(request.end_date)}</p>
                <p className="mt-2 text-sm text-slate-500">{request.reason}</p>
                {request.status !== "canceled" ? <Button type="button" variant="outline" className="mt-4 rounded-2xl" onClick={() => cancelLeave(request.id)} data-testid={`employee-leave-cancel-${request.id}`}>Cancel</Button> : null}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
    )
  );

  const renderRegularization = () => (
    <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
      <Card className="border-slate-200/70 bg-white/85" data-testid="employee-regularization-form-card">
        <CardHeader>
          <CardTitle data-testid="employee-regularization-form-title">Submit regularization</CardTitle>
          <CardDescription data-testid="employee-regularization-form-description">Use this when you forgot a punch or need a correction workflow.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submitRegularization} data-testid="employee-regularization-form">
            <Input type="date" value={regularizationForm.date} onChange={(event) => setRegularizationForm((current) => ({ ...current, date: event.target.value }))} data-testid="employee-regularization-date-input" />
            <div className="grid gap-3 md:grid-cols-2">
              <Input type="time" value={regularizationForm.requested_clock_in_time} onChange={(event) => setRegularizationForm((current) => ({ ...current, requested_clock_in_time: event.target.value }))} data-testid="employee-regularization-clockin-input" />
              <Input type="time" value={regularizationForm.requested_clock_out_time} onChange={(event) => setRegularizationForm((current) => ({ ...current, requested_clock_out_time: event.target.value }))} data-testid="employee-regularization-clockout-input" />
            </div>
            <Input value={regularizationForm.reason} onChange={(event) => setRegularizationForm((current) => ({ ...current, reason: event.target.value }))} placeholder="Reason" data-testid="employee-regularization-reason-input" />
            <Button type="submit" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" data-testid="employee-regularization-submit-button">Submit regularization</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-slate-200/70 bg-white/85" data-testid="employee-regularization-history-card">
        <CardHeader>
          <CardTitle data-testid="employee-regularization-history-title">Regularization status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {regularizations.map((request) => (
            <div key={request.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid={`employee-regularization-item-${request.id}`}>
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold text-slate-900">{formatDate(request.date)}</p>
                <StatusBadge value={request.status} testId={`employee-regularization-status-${request.id}`} />
              </div>
              <p className="mt-2 text-sm text-slate-500">{extractTime(request.requested_clock_in)} → {extractTime(request.requested_clock_out)}</p>
              <p className="mt-2 text-sm text-slate-500">{request.reason}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );

  const renderScan = () => (
    <Card className="border-slate-200/70 bg-white/85" data-testid="employee-scan-card">
      <CardHeader>
        <CardTitle data-testid="employee-scan-title">{assignedFeature === "kiosk_qr_attendance" ? "Kiosk QR scanner" : "Employee QR scanner"}</CardTitle>
        <CardDescription data-testid="employee-scan-description">Use your assigned QR flow below. You can scan live codes with the camera or paste a token manually.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4" data-testid="employee-scan-assignment-card">
          <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-400" data-testid="employee-scan-assignment-caption">Assigned attendance method</p>
          <p className="mt-2 text-lg font-bold text-slate-900" data-testid="employee-scan-assignment-value">{today?.employee?.assigned_attendance_label || assignedFeatureLabel}</p>
        </div>

        {tenantHasScanFeature && isScannerAttendanceFeature(assignedFeature) ? (
          <>
            <QrScannerPanel onDetected={handleScanDetected} />
            <div className="grid gap-4 md:grid-cols-[1fr_auto]" data-testid="employee-scan-form">
              <Input value={scanToken} onChange={(event) => setScanToken(event.target.value)} placeholder="Paste kiosk session token" data-testid="employee-scan-token-input" />
              <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={goToScan} data-testid="employee-scan-go-button">Open scan page</Button>
            </div>
          </>
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 text-slate-500" data-testid="employee-scan-disabled-message">{today?.quick_action?.error || "QR attendance is not available for your assigned method right now."}</div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <AppShell title={section === "dashboard" ? "My workspace" : section === "attendance" ? "My attendance history" : section === "leave" ? "Leave management" : section === "regularization" ? "Attendance corrections" : "QR attendance"} subtitle={section === "dashboard" ? "See your punch state, hours, balances, and quick actions in one browser-friendly view." : section === "attendance" ? "Track your punches, total hours, late minutes, and source history." : section === "leave" ? "Apply, cancel, and review leave requests with live balance visibility." : section === "regularization" ? "Submit corrections for missed or inaccurate punches and track approval status." : "Use the employee QR scan flow when your tenant has it enabled."} navItems={navItems} user={user} tenant={tenant} onLogout={logout}>
      {loading ? <Card className="border-slate-200/70 bg-white/85 p-10 text-center text-slate-500" data-testid="employee-loading-card">Loading your workspace...</Card> : null}
      {!loading && section === "dashboard" ? renderDashboard() : null}
      {!loading && section === "attendance" ? renderAttendance() : null}
      {!loading && section === "leave" ? renderLeave() : null}
      {!loading && section === "regularization" ? renderRegularization() : null}
      {!loading && section === "scan" ? renderScan() : null}
    </AppShell>
  );
}